/**
 * The 10x10 board.
 *
 * Rendered from state rather than assembled as an HTML string. The original
 * built the whole table with string concatenation on every change and then
 * mutated classes imperatively in a second pass (`highlightWinners`), which is
 * why winner styling could drift out of sync with the grid it was applied to.
 */

import { memo, useMemo } from 'react';
import { winningSquares, AXIS_LENGTH, cellIndex } from '../../shared/game-logic';
import type { GameState, Player } from '../../shared/types';

interface Props {
  state: GameState;
  /** Admin-only manual assignment: clicking a square assigns this player. */
  assigningPlayerId: string | null;
  /**
   * The viewer, so their own squares can be marked.
   *
   * Finding your squares among a hundred two-letter monograms is genuinely
   * hard, and it is the thing a player looks for first.
   */
  viewerId?: string;
  onCellClick?: (index: number) => void;
}

function SquaresGridImpl({ state, assigningPlayerId, viewerId, onCellClick }: Props) {
  const playersById = useMemo(
    () => new Map(state.players.map((p) => [p.id, p] as const)),
    [state.players],
  );

  const winners = useMemo(() => winningSquares(state), [state]);
  const winnerByIndex = useMemo(() => {
    const map = new Map<number, 'final' | 'potential'>();
    for (const w of winners) {
      // A final result outranks a live highlight on the same square.
      if (w.kind === 'final' || !map.has(w.index)) map.set(w.index, w.kind);
    }
    return map;
  }, [winners]);

  const revealed = state.axesRevealed && state.axes.home.length === AXIS_LENGTH;
  const homeAbbr = state.config.teams.home.abbr;
  const awayAbbr = state.config.teams.away.abbr;
  const interactive = Boolean(onCellClick && assigningPlayerId !== undefined);

  return (
    <div className="grid-wrap">
      <div
        className="grid"
        role="grid"
        aria-label={`Squares board. ${homeAbbr} digits down the side, ${awayAbbr} digits across the top.`}
      >
        <div className="grid-cell corner" role="columnheader">
          {revealed ? `${homeAbbr} ↓ / ${awayAbbr} →` : '—'}
        </div>

        {Array.from({ length: AXIS_LENGTH }, (_, col) => (
          <div key={`ah-${col}`} className="grid-cell axis axis-away" role="columnheader">
            {revealed ? state.axes.away[col] : '?'}
          </div>
        ))}

        {Array.from({ length: AXIS_LENGTH }, (_, row) => (
          <Row
            key={`row-${row}`}
            row={row}
            revealed={revealed}
            homeDigit={state.axes.home[row]}
            grid={state.grid}
            playersById={playersById}
            winnerByIndex={winnerByIndex}
            interactive={interactive}
            onCellClick={onCellClick}
            homeAbbr={homeAbbr}
            awayAbbr={awayAbbr}
            awayAxis={state.axes.away}
            viewerId={viewerId}
          />
        ))}
      </div>
    </div>
  );
}

interface RowProps {
  row: number;
  revealed: boolean;
  homeDigit: number | undefined;
  grid: (string | null)[];
  playersById: Map<string, Player>;
  winnerByIndex: Map<number, 'final' | 'potential'>;
  interactive: boolean;
  onCellClick?: (index: number) => void;
  homeAbbr: string;
  awayAbbr: string;
  awayAxis: number[];
  viewerId?: string;
}

function Row({
  row,
  revealed,
  homeDigit,
  grid,
  playersById,
  winnerByIndex,
  interactive,
  onCellClick,
  homeAbbr,
  awayAbbr,
  awayAxis,
  viewerId,
}: RowProps) {
  return (
    <>
      <div className="grid-cell axis axis-home" role="rowheader">
        {revealed ? homeDigit : '?'}
      </div>
      {Array.from({ length: AXIS_LENGTH }, (_, col) => {
        const index = cellIndex(row, col);
        const owner = grid[index] ? playersById.get(grid[index]!) : undefined;
        const winner = winnerByIndex.get(index);

        const isMine = Boolean(viewerId) && grid[index] === viewerId;
        const classes = [
          'grid-cell',
          owner ? 'taken' : 'empty',
          isMine ? 'mine' : '',
          winner === 'final' ? 'winner' : '',
          winner === 'potential' ? 'potential' : '',
          interactive ? 'selectable' : '',
        ]
          .filter(Boolean)
          .join(' ');

        const digits = revealed
          ? `${homeAbbr} ${homeDigit}, ${awayAbbr} ${awayAxis[col]}`
          : 'digits hidden';
        const label = `${digits}. ${owner ? owner.name : 'Unclaimed'}${
          isMine ? '. Yours' : ''
        }${winner === 'final' ? '. Winning square' : ''}${
          winner === 'potential' ? '. Currently winning' : ''
        }`;

        const content = owner ? initials(owner.name) : '';

        return interactive ? (
          <button
            key={index}
            type="button"
            className={classes}
            style={owner ? { background: owner.color } : undefined}
            onClick={() => onCellClick?.(index)}
            aria-label={label}
            title={owner?.name ?? 'Unclaimed'}
          >
            {content}
          </button>
        ) : (
          <div
            key={index}
            className={classes}
            style={owner ? { background: owner.color } : undefined}
            role="gridcell"
            aria-label={label}
            title={owner?.name ?? 'Unclaimed'}
          >
            {content}
          </div>
        );
      })}
    </>
  );
}

/** "Drew Coffey" -> "DC". Single-word names keep their first three letters. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!.slice(0, 3).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export const SquaresGrid = memo(SquaresGridImpl);
